import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import TripRouteAccordionCombined from '@/components/modules/trip-route-details/trip-route-accordion/trip-route-accordion-combined/trip-route-accordion-combined';
import TripRouteAccordionSingle from '@/components/modules/trip-route-details/trip-route-accordion/trip-route-accordion-single/trip-route-accordion-single';
import { InteractedItem } from '@/components/modules/trip-route-details/trip-route-details';
import styles from './trip-route-accordion.module.scss';

export interface TripRouteAccordionProps {
  /** The class name to apply */
  className?: string;
  /** The interacted item */
  interactedItem?: InteractedItem;
  /** Indicates whether the accordion is open */
  isActive?: boolean;
  /** The accordion item */
  item?: Content.Properties.DescriptionItem;
  /** The identification for the accordion */
  itemIndex?: number;
  /** An event that triggers scroll */
  onChangeShouldScroll?: (value?: boolean) => void;
  /** An event that is triggered on click */
  onClick?: () => void;
  /** An event that is triggered when accordion item is selected */
  onSelect?: (id?: string) => void;
  /** The sub items to combine in the accordion block */
  subItems?: (
    | Content.Properties.DescriptionItem
    | Content.Properties.TransportModeItem
  )[];
}

const TripRouteAccordion = ({
  className,
  interactedItem,
  isActive,
  item,
  itemIndex,
  onChangeShouldScroll,
  onClick,
  onSelect,
  subItems = [],
}: TripRouteAccordionProps) => {
  const hasSingleItem = !!item;
  const hasSubItems = Array.isArray(subItems) && subItems.length > 0;

  const [isAccordionActive, setIsAccordionActive] = useState<
    boolean | undefined
  >(false);
  const [isCombinedItemExpanded, setIsCombinedItemExpanded] = useState(false);

  const sharedProps = {
    interactedItem,
    isActive,
    itemIndex,
    onClick,
    onSelect,
  };

  useEffect(() => {
    if (hasSingleItem) {
      setIsAccordionActive(isActive);
    }

    if (hasSubItems) {
      setIsAccordionActive(isCombinedItemExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isCombinedItemExpanded]);

  useEffect(() => {
    if (!onChangeShouldScroll) return;

    if (isAccordionActive) {
      onChangeShouldScroll(isAccordionActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccordionActive]);

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isAccordionActive]: isAccordionActive,
      })}
      id={isAccordionActive ? `accordion-active` : ''}
    >
      {hasSingleItem && (
        <TripRouteAccordionSingle {...sharedProps} item={item} />
      )}

      {hasSubItems && (
        <TripRouteAccordionCombined
          {...sharedProps}
          onChange={value => setIsCombinedItemExpanded(value)}
          subItems={subItems}
        />
      )}
    </div>
  );
};

TripRouteAccordion.displayName = 'TripRouteAccordion';

export default TripRouteAccordion;
